import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  Text,
  Flex,
  HStack,
  VStack,
  SimpleGrid,
  Button,
  IconButton,
  Badge,
  Spinner,
  Divider,
  Input,
  Checkbox,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon, EditIcon } from '@chakra-ui/icons';
import { FiUsers } from 'react-icons/fi';
import { useQuery } from 'react-query';
import { groupsApi, Group } from '../services/api';

// Modal de edição de um grupo: nome (leitura), membros e coleções.
const GroupEditModal = ({
  group,
  isOpen,
  onClose,
  onSaved,
}: {
  group: Group | null;
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
}) => {
  const toast = useToast();
  const { data: options } = useQuery('group-options', groupsApi.options, { staleTime: 60000 });
  const [members, setMembers] = useState<Set<string>>(new Set());
  const [collections, setCollections] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen || !group) return;
    (async () => {
      const d = await groupsApi.detail(group.id);
      setMembers(new Set(d.members));
      setCollections(new Set(d.collections));
    })();
  }, [isOpen, group]);

  const toggle = (set: Set<string>, setter: (s: Set<string>) => void, val: string) => {
    const next = new Set(set);
    if (next.has(val)) next.delete(val);
    else next.add(val);
    setter(next);
  };

  const save = async () => {
    if (!group) return;
    setSaving(true);
    try {
      await groupsApi.setMembers(group.id, Array.from(members));
      await groupsApi.setCollections(group.id, Array.from(collections));
      toast({ title: 'Grupo salvo', status: 'success', duration: 2500, isClosable: true });
      onSaved();
      onClose();
    } catch {
      toast({ title: 'Erro ao salvar', status: 'error', duration: 3000, isClosable: true });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Grupo: {group?.name}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            <Box>
              <Text fontWeight="semibold" fontSize="sm" mb={2}>
                Membros
              </Text>
              <VStack align="stretch" spacing={1} maxH="320px" overflowY="auto">
                {(options?.users || []).map((u) => (
                  <Checkbox
                    key={u.email}
                    isChecked={members.has(u.email)}
                    onChange={() => toggle(members, setMembers, u.email)}
                    colorScheme="brand"
                  >
                    <Text fontSize="sm">
                      {u.name} <Text as="span" color="gray.400">({u.email})</Text>
                    </Text>
                  </Checkbox>
                ))}
                {(options?.users || []).length === 0 && (
                  <Text fontSize="sm" color="gray.400">Nenhum usuário na organização.</Text>
                )}
              </VStack>
            </Box>
            <Box>
              <Text fontWeight="semibold" fontSize="sm" mb={2}>
                Coleções acessíveis
              </Text>
              <VStack align="stretch" spacing={1} maxH="320px" overflowY="auto">
                {(options?.collections || []).map((c) => (
                  <Checkbox
                    key={c}
                    isChecked={collections.has(c)}
                    onChange={() => toggle(collections, setCollections, c)}
                    colorScheme="brand"
                  >
                    <Text fontSize="sm">{c}</Text>
                  </Checkbox>
                ))}
                {(options?.collections || []).length === 0 && (
                  <Text fontSize="sm" color="gray.400">Nenhuma coleção na organização.</Text>
                )}
              </VStack>
            </Box>
          </SimpleGrid>
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="brand" size="sm" onClick={save} isLoading={saving}>
            Salvar ({members.size} membros · {collections.size} coleções)
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const Groups = () => {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [editing, setEditing] = useState<Group | null>(null);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);

  const { data: groups = [], refetch, isLoading } = useQuery('groups', groupsApi.list);

  const create = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      await groupsApi.create(newName.trim());
      setNewName('');
      refetch();
    } catch {
      toast({ title: 'Erro ao criar grupo', status: 'error', duration: 3000, isClosable: true });
    } finally {
      setCreating(false);
    }
  };

  const remove = async (g: Group) => {
    if (!window.confirm(`Excluir o grupo "${g.name}"?`)) return;
    try {
      await groupsApi.remove(g.id);
      refetch();
    } catch {
      toast({ title: 'Erro ao excluir', status: 'error', duration: 3000, isClosable: true });
    }
  };

  const openEdit = (g: Group) => {
    setEditing(g);
    onOpen();
  };

  return (
    <Box>
      <Heading size="lg" color="brand.700" mb={1}>
        Grupos
      </Heading>
      <Text color="gray.600" mb={6}>
        Organize usuários em grupos e defina quais coleções cada grupo pode acessar. Membros veem e conversam
        apenas com as coleções dos seus grupos; administradores veem todas.
      </Text>

      <FormControl mb={6}>
        <FormLabel fontSize="sm">Novo grupo</FormLabel>
        <HStack>
          <Input
            placeholder="Ex.: Financeiro"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && create()}
            size="sm"
            maxW="320px"
          />
          <Button leftIcon={<AddIcon />} colorScheme="brand" size="sm" onClick={create} isLoading={creating}>
            Criar
          </Button>
        </HStack>
      </FormControl>

      <Divider mb={6} />

      {isLoading ? (
        <Flex justify="center" py={16}>
          <Spinner color="brand.500" />
        </Flex>
      ) : groups.length === 0 ? (
        <Box borderWidth="1px" borderRadius="lg" borderStyle="dashed" py={12} px={6} textAlign="center">
          <VStack spacing={3}>
            <Flex bg="brand.50" boxSize="64px" borderRadius="full" align="center" justify="center">
              <FiUsers size={26} color="#6B46C1" />
            </Flex>
            <Box>
              <Heading size="md" color="gray.700">Crie seu primeiro grupo</Heading>
              <Text color="gray.500" mt={1} maxW="lg" mx="auto">
                Grupos controlam quem acessa cada base. Crie um grupo e dê acesso às bases certas.
              </Text>
            </Box>
          </VStack>
        </Box>
      ) : (
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
          {groups.map((g) => (
            <Box key={g.id} borderWidth="1px" borderRadius="lg" p={4} bg="white" shadow="sm">
              <Flex justify="space-between" align="center">
                <HStack>
                  <FiUsers />
                  <Text fontWeight="semibold">{g.name}</Text>
                </HStack>
                <HStack spacing={1}>
                  <IconButton aria-label="Editar" icon={<EditIcon />} size="xs" variant="ghost" onClick={() => openEdit(g)} />
                  <IconButton aria-label="Excluir" icon={<DeleteIcon />} size="xs" variant="ghost" colorScheme="red" onClick={() => remove(g)} />
                </HStack>
              </Flex>
              <HStack mt={2} spacing={2}>
                <Badge colorScheme="blue">{g.members} membros</Badge>
                <Badge colorScheme="purple">{g.collections} coleções</Badge>
              </HStack>
            </Box>
          ))}
        </SimpleGrid>
      )}

      <GroupEditModal group={editing} isOpen={isOpen} onClose={onClose} onSaved={refetch} />
    </Box>
  );
};

export default Groups;
